import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    Stack,
    SxProps,
    Typography,
} from '@mui/material';
import { headingFontBold, labelFontSemiBold, vars } from '../../../../Theme';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import { IncludeInExportType } from '../../../../redux/project/documents/sliceProjectAdvancedExport';
import CheckboxTree from './CheckboxTree';
import { v4 as uuidv4 } from 'uuid';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    color: vars.black,
    fontSize: '16px',
    marginBottom: '8px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

export default ({ mainState, handleChange, showErrorState }: IContentProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'dialogParts' });

    const fileFormatNameOptions = useSelector(
        (_store: IAppReduxStore) => _store.projectAdvancedExportDocuments.formatFileNameOptions,
    );

    const exportAdditionalDocumentOptions = useSelector(
        (_store: IAppReduxStore) =>
            _store.projectAdvancedExportDocuments.exportAdditionalDocumentOptions,
    );

    const includeInExportOptions = useSelector(
        (_store: IAppReduxStore) => _store.projectAdvancedExportDocuments.includeInExportOptions,
    );

    const [additionalDocsSelected, setAdditionalDocsSelected] = useState<boolean>(false);
    const [fullCheckedDocTypeVariantsIds, setFullCheckedDocTypeVariantsIds] = useState<string[]>(
        [],
    );

    useEffect(() => {
        !mainState.documentTypeName &&
            handleChange({
                field: 'documentTypeName',
                value: fileFormatNameOptions[0]?.key,
            });
    }, [mainState.documentTypeName]);

    useEffect(() => {
        fileFormatNameOptions &&
            handleChange({
                field: 'documentTypeName',
                value: fileFormatNameOptions[0].key,
            });
    }, [fileFormatNameOptions]);

    useEffect(() => {
        handleChange({
            field: 'additionalFiles',
            value: additionalDocsSelected ? selectedDocVariants : [],
        });
    }, [fullCheckedDocTypeVariantsIds, additionalDocsSelected]);

    const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>, field: string) => {
        const { value, checked } = event.target;
        handleChange({
            field: field,
            value: mainState[field].filter(opt => opt !== value).concat(checked ? value : []),
        });
    };

    const handleAdditionalDocumentsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAdditionalDocsSelected(event.target.value === 'yes');
    };

    const handleSelectDeselectAll = (_, checked: boolean) => {
        checked
            ? setFullCheckedDocTypeVariantsIds(items.map(item => item.id))
            : setFullCheckedDocTypeVariantsIds([]);
    };

    const docTypeVariants = exportAdditionalDocumentOptions?.flatMap(
        exportAdditionalDocumentOption => {
            const docTypeVariants = [];

            docTypeVariants.push({
                parent: exportAdditionalDocumentOption.docType,
                parentName: exportAdditionalDocumentOption.docTypeName,
                child: undefined,
            });

            if (exportAdditionalDocumentOption.docVariants.length > 0) {
                docTypeVariants.push(
                    ...exportAdditionalDocumentOption.docVariants.map(docVariant => {
                        return {
                            parent: exportAdditionalDocumentOption.docType,
                            parentName: exportAdditionalDocumentOption.docTypeName,
                            child: docVariant,
                        };
                    }),
                );
            }

            return docTypeVariants;
        },
    );

    const items = useMemo(() => {
        if (docTypeVariants?.length >= 0) {
            return docTypeVariants.map(item => {
                const children = exportAdditionalDocumentOptions
                    .filter(x => x.docType === item.parent)
                    .flatMap(x => x.docVariants);

                return {
                    children: children,
                    id: uuidv4(),
                    label: item.child ?? item.parentName,
                    parent: item.child ? item.parentName : undefined,
                    path: item.child ? `${item.parentName + '\\' + item.child}` : item.parentName,
                    name: item.child ?? item.parent,
                };
            });
        }
    }, [exportAdditionalDocumentOptions]);

    const selectedDocVariants = useMemo(() => {
        if (fullCheckedDocTypeVariantsIds.length > 0) {
            const docTypes = items?.filter(
                item => fullCheckedDocTypeVariantsIds.includes(item.id) && !item?.parent,
            );

            return docTypes.flatMap(docType => {
                const filteredItems = items?.filter(
                    item =>
                        fullCheckedDocTypeVariantsIds.includes(item.id) &&
                        docType.label === item.parent,
                );

                return [
                    {
                        docType: docType.name,
                        docVariants:
                            filteredItems?.length > 0
                                ? filteredItems.map(item => item.name)
                                : [docType.label],
                    },
                ];
            });
        }
    }, [fullCheckedDocTypeVariantsIds]);

    const parentChildrenIdCheck = (id: string, isChecked: boolean, treeData: any) => {
        const foundTreeDataElement = treeData.find(x => x.id === id);
        const ids: string[] = [];

        if (!foundTreeDataElement) {
            treeData.map(root => {
                const treeElement: any = root;

                if (treeElement?.children?.length > 0 && treeElement?.id !== id) {
                    parentChildrenIdCheck(id, isChecked, treeElement.children);
                }
            });

            return;
        } else {
            const parentLabel = items.find(item => item.id === id).parent;

            const parentId =
                foundTreeDataElement.parent &&
                items.find(item => item.label === foundTreeDataElement.parent).id;

            let siblingLength = 0;

            fullCheckedDocTypeVariantsIds.map(docTypeVariantsId => {
                const selectedItemLabel = items.find(
                    item => item.id === docTypeVariantsId && item.id !== parentId,
                )?.parent;

                if (selectedItemLabel === parentLabel) {
                    return siblingLength++;
                }
            });

            if (typeof parentId === 'string' && siblingLength < 2) {
                ids.push(parentId);
            }

            ids.push(id, ...foundTreeDataElement.children.map(x => x.id));
        }

        setFullCheckedDocTypeVariantsIds(prevIds =>
            prevIds.filter(id => !ids.includes(id)).concat(isChecked ? ids : []),
        );
    };

    return (
        <Stack direction='column' spacing={'16px'} sx={{ width: '512px' }}>
            <FormControl size='small'>
                <FormLabel sx={{ ...formLabelSx }}>{t('selectDocType')}</FormLabel>
                <Select
                    value={mainState.documentTypeName}
                    error={showErrorState.documentTypeName}
                    onChange={(e: SelectChangeEvent<string>) => {
                        handleChange({
                            field: 'documentTypeName',
                            value: e.target.value,
                        });
                    }}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                >
                    {fileFormatNameOptions?.map(option => (
                        <MenuItem key={option.key} value={option.key}>
                            {option.display}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl size='small'>
                <FormLabel sx={{ ...formLabelSx }}>{t('includeInExport')}</FormLabel>
                <FormGroup
                    sx={{
                        borderRadius: '4px',
                        border: `1px solid ${vars.lightGray}`,
                        px: '12px',
                    }}
                >
                    {includeInExportOptions?.map(item => (
                        <FormControlLabel
                            key={item.id}
                            control={
                                <Checkbox
                                    checked={mainState.includeInExport.includes(item.id)}
                                    onChange={e => {
                                        handleChangeCheckbox(e, 'includeInExport');
                                    }}
                                    value={item.id}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        ...labelFontSemiBold,
                                        fontSize: '16px',
                                        color: vars.font,
                                        fontWeight: 700,
                                    }}
                                >
                                    {t(item.translationKey)}
                                </Typography>
                            }
                        />
                    ))}
                </FormGroup>
            </FormControl>
            <FormControl size='small'>
                <FormLabel sx={{ ...formLabelSx }}>{t('addAdditionalFiles')}</FormLabel>
                <RadioGroup
                    row
                    sx={{
                        borderRadius: '4px',
                        border: `1px solid ${vars.lightGray}`,
                        px: '12px',
                    }}
                    defaultValue={'no'}
                >
                    {['no', 'yes'].map(option => (
                        <FormControlLabel
                            key={option}
                            value={option}
                            control={<Radio onChange={handleAdditionalDocumentsChange} />}
                            label={t(option)}
                        />
                    ))}
                </RadioGroup>
            </FormControl>
            {additionalDocsSelected && (
                <FormControl>
                    <Stack
                        direction='row'
                        sx={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <FormLabel sx={{ ...formLabelSx }}>
                            {t('chooseAdditionalDocTypeVariants')}
                        </FormLabel>
                        <FormControlLabel
                            label={
                                docTypeVariants.length !== fullCheckedDocTypeVariantsIds.length
                                    ? t('selectAll')
                                    : t('clear')
                            }
                            labelPlacement='start'
                            sx={{
                                marginBottom: '8px',
                                '& .MuiFormControlLabel-label': {
                                    fontFamily: labelFontSemiBold,
                                    fontSize: '14px',
                                },
                            }}
                            control={
                                <Checkbox
                                    sx={{ padding: 0, paddingX: '9px' }}
                                    onChange={handleSelectDeselectAll}
                                    checked={
                                        docTypeVariants.length ===
                                        fullCheckedDocTypeVariantsIds.length
                                    }
                                />
                            }
                        />
                    </Stack>

                    <CheckboxTree
                        items={items}
                        checkedNodes={fullCheckedDocTypeVariantsIds}
                        onCheckNode={parentChildrenIdCheck}
                    />
                </FormControl>
            )}
        </Stack>
    );
};

export interface IAdvancedExportMainState {
    documentTypeName: string;
    includeInExport: IncludeInExportType[];
    additionalFiles: IExportAdditionalDocument[];
}

export interface IAdvancedExportErrorState {
    documentTypeName: boolean;
}

export interface IExportAdditionalDocument {
    docType: string;
    docVariants: string[];
}

interface IContentProps {
    mainState: IAdvancedExportMainState;
    handleChange: ({
        field,
        value,
    }: {
        field: string;
        value: string | string[] | IExportAdditionalDocument | IExportAdditionalDocument[];
    }) => void;
    showErrorState: IAdvancedExportErrorState;
}
